import React, { useState, useEffect } from "react";
import ProspectPropertyService from "../../../../../Service/ProspectPropertyService";
import { FiSearch } from "react-icons/fi";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/Constant/Notification";
import { userUid } from "../../../../../../utils/storage";

const UserSelectionModal = ({
  isOpen,
  onClose,
  title,
  users,
  onSelect,
  selectedUserId,
}) => {
  const [searchTerm, setSearchTerm] = React.useState("");

  if (!isOpen) return null;

  const filteredUsers = users.filter((user) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderRing = (percentage, color) => (
    <div
      className="relative w-6 h-6 rounded-full"
      style={{
        background: `conic-gradient(${color} ${percentage}%, #E5E7EB ${percentage}%)`,
      }}
    >
      <div className="absolute inset-[3px] bg-white rounded-full"></div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[900px] max-h-[600px] overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            ×
          </button>
        </div>

        <div className="relative mb-4">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-4 pr-14 h-[50px] border border-gray-300 rounded-[10px] shadow-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-customGreen"
          />
          <button
            type="submit"
            className="absolute right-0 top-0 h-[50px] w-[50px] bg-customGreen text-white rounded-r-[10px] flex items-center justify-center hover:bg-green-600 transition"
          >
            <FiSearch size={20} />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[400px]">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No users found matching "{searchTerm}"
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div
                key={user.id}
                className={`bg-white cursor-pointer hover:bg-green-100 hover:border-green-500 overflow-y-auto shadow-soft-all p-4 rounded-[10px] mb-3 border ${
                  selectedUserId === user.id
                    ? "border-green-500"
                    : "border-gray-200"
                }`}
                onClick={() => onSelect(user)} // ✅ makes whole div clickable
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center text-nowrap gap-3">
                    
                    <input
                      type="radio"
                      name={`${title}-user`}
                      value={user.id}
                      checked={selectedUserId === user.id}
                      onChange={() => onSelect(user)}
                      onClick={(e) => e.stopPropagation()} // ✅ prevents duplicate triggers
                      className="w-5 h-5 hidden"
                    />
                    <div className="flex flex-col">
                      <div className="text-sm font-medium truncate">
                        {user.name}
                      </div>
                      <div className="text-[12px] text-green-600">
                        (Ongoing Follow ups {user.onGoingCount})
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      {renderRing(user.followupOn.percentage, "#FACC15")}
                      {user.followupOn.count} {user.followupOn.label}{" "}
                      {user.followupOn.percentage}%
                    </div>
                    <div className="flex text-nowrap items-center gap-2">
                      {renderRing(user.followupto.percentage, "#8B5CF6")}
                      {user.followupto.count} {user.followupto.label}{" "}
                      {user.followupto.percentage}%
                    </div>
                    <div className="flex items-center gap-2">
                      {renderRing(user.followupAbove.percentage, "#3B82F6")}
                      {user.followupAbove.count} {user.followupAbove.label}{" "}
                      {user.followupAbove.percentage}%
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const AssignFrontendTeam = ({
  selectedFrontendUser,
  setSelectedFrontendUser,
  propertyData,
  onSelectedUsersChange,
  isDropped,
  propertyId,
  setPropertyData,
  onRefresh,
}) => {
  const [userData, setUserData] = useState([]);
  const [modalState, setModalState] = useState({ isOpen: false, type: null });
  const [selectedUsers, setSelectedUsers] = useState({
    legalAnalysis: null,
    advocateLegalAnalysis: null,
    vaoLegalAnalysis: null,
    sroLegalAnalysis: null,
    physicalInspection: null,
  });
  const [saving, setSaving] = useState(false);

  const exitingData = propertyData?.frontendUserDetails || {};

  // Pre-fill selected users from API response
  useEffect(() => {
    if (exitingData) {
      setSelectedUsers((prev) => ({
        ...prev,
        advocateLegalAnalysis:
          exitingData.advocateLegalAnalysisUser &&
          exitingData.advocateLegalAnalysisUser.value &&
          exitingData.advocateLegalAnalysisUser.label
            ? {
                id: exitingData.advocateLegalAnalysisUser.value,
                name: exitingData.advocateLegalAnalysisUser.label,
              }
            : null,
        vaoLegalAnalysis:
          exitingData.vaoLegalAnalysisUser &&
          exitingData.vaoLegalAnalysisUser.value &&
          exitingData.vaoLegalAnalysisUser.label
            ? {
                id: exitingData.vaoLegalAnalysisUser.value,
                name: exitingData.vaoLegalAnalysisUser.label,
              }
            : null,
        sroLegalAnalysis:
          exitingData.sroLegalAnalysisUser &&
          exitingData.sroLegalAnalysisUser.value &&
          exitingData.sroLegalAnalysisUser.label
            ? {
                id: exitingData.sroLegalAnalysisUser.value,
                name: exitingData.sroLegalAnalysisUser.label,
              }
            : null,
        physicalInspection:
          exitingData.physicalInspectionUser &&
          exitingData.physicalInspectionUser.value &&
          exitingData.physicalInspectionUser.label
            ? {
                id: exitingData.physicalInspectionUser.value,
                name: exitingData.physicalInspectionUser.label,
              }
            : null,
      }));
    }
  }, [propertyData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ProspectPropertyService.getFrontendUsersList();
        setUserData(response.data?.users || []);
      } catch (error) {
        console.error("Error fetching frontend users list:", error);
      }
    };
    fetchData();
  }, []);

  // Pass selected users data to parent component
  useEffect(() => {
    if (onSelectedUsersChange) {
      onSelectedUsersChange(selectedUsers);
    }
  }, [selectedUsers, onSelectedUsersChange]);

  const data = userData.map((user) => ({
    id: user.id,
    name: user.name || "John Doe",
    onGoingCount: user.statusCounts?.overAll || 0,
    followupOn: {
      count: user.statusCounts?.status2,
      percentage: 20,
      label: "Follow ups on",
    },
    followupto: {
      count: user.statusCounts?.status1,
      percentage: 40,
      label: "Follow ups to",
    },
    followupAbove: {
      count: user.statusCounts?.status3,
      percentage: 60,
      label: "Follow ups above",
    },
  }));

  const userTypes = [
    {
      key: "advocateLegalAnalysis",
      title: "Assign Advocate Legal Analysis User",
      label: "Advocate Legal Analysis User",
    },
    {
      key: "vaoLegalAnalysis",
      title: "Assign VAO Legal Analysis User",
      label: "VAO Legal Analysis User",
    },
    {
      key: "sroLegalAnalysis",
      title: "Assign SRO Legal Analysis User",
      label: "SRO Legal Analysis User",
    },
    {
      key: "physicalInspection",
      title: "Assign Physical Inspection & General Legal Analysis User",
      label: "Physical Inspection & General Legal Analysis User",
    },
  ];

  const openModal = (type) => {
    if (!isDropped) {
      setModalState({ isOpen: true, type });
    }
  };

  const closeModal = () => {
    setModalState({ isOpen: false, type: null });
  };

  // Function to refresh property data from API
  const refreshPropertyData = async () => {
    try {
      const response = await ProspectPropertyService.getStageWiseProperties({
        propertyId: propertyId,
        stage: "Info Collection & Assignment",
      });
      const data = response.data?.data;
      if (data && setPropertyData) {
        setPropertyData(data);
      }
    } catch (error) {
      console.error("Error refreshing property data:", error);
    }
  };

  const saveUserAssignment = async (updatedUsers) => {
    if (!propertyId) return;

    setSaving(true);
    try {
      const body = {
        propertyId: propertyId,
        modifiedById: userUid,
        frontendTeamObj: {
          advocateLegalAnalysisUser: updatedUsers?.advocateLegalAnalysis?.id,
          vaoLegalAnalysisUser: updatedUsers?.vaoLegalAnalysis?.id,
          sroLegalAnalysisUser: updatedUsers?.sroLegalAnalysis?.id,
          physicalInspectionUser: updatedUsers?.physicalInspection?.id,
        },
      };

      const response =
        await ProspectPropertyService.updateFrontendUserAssignment(body);

      if (response.status === 200 || response.status === 201) {
        showSuccessToast("Team assignment updated successfully!");
        // Refresh property data to get latest state
        await refreshPropertyData();
        // Also trigger parent refresh for comprehensive update
        if (onRefresh) {
          await onRefresh();
        }
      }
    } catch (error) {
      console.error("Error saving user assignment:", error);
      showErrorToast(
        error.response?.data?.message || "Error updating team assignment"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleUserSelect = async (user) => {
    const updatedUsers = {
      ...selectedUsers,
      [modalState.type]: user,
    };

    setSelectedUsers(updatedUsers);
    closeModal();

    // Save immediately to API
    await saveUserAssignment(updatedUsers);
  };

  const currentModalType = userTypes.find(
    (type) => type.key === modalState.type
  );

  return (
    <div className="p-4">
      <p className="text-sm text-slate-500 mb-6">
        Assign team members for different aspects of property analysis
      </p>

      <div className=" grid grid-cols-2 gap-4 mb-4 items-center">
        {userTypes.map((userType) => (
          <div key={userType.key} className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">
              {userType.title}
            </h3>

            {selectedUsers[userType.key] ? (
              <div className="w-full border border-gray-300 rounded-lg p-3 flex items-center justify-between bg-blue-50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-medium text-sm">
                      {selectedUsers[userType.key]?.name
                        ?.charAt(0)
                        ?.toUpperCase() || "?"}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedUsers[userType.key]?.name || "Unknown User"}
                    </p>
                    {/* <p className="text-xs text-gray-500">
                      Ongoing: {selectedUsers[userType.key].onGoingCount}
                    </p> */}
                  </div>
                </div>
                <button
                  onClick={async () => {
                    const updatedUsers = {
                      ...selectedUsers,
                      [userType.key]: null,
                    };
                    setSelectedUsers(updatedUsers);
                    // Save immediately to API
                    await saveUserAssignment(updatedUsers);
                  }}
                  disabled={isDropped || saving}
                  className={`text-sm font-medium ${
                    isDropped || saving
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-red-500 hover:text-red-700"
                  }`}
                >
                  {saving ? "Removing..." : "Remove"}
                </button>
              </div>
            ) : (
              <button
                onClick={() => openModal(userType.key)}
                disabled={isDropped || saving}
                className={`w-full border rounded-lg p-3 text-left transition-colors ${
                  isDropped || saving
                    ? "border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "border-gray-300 text-gray-500 hover:bg-gray-50"
                }`}
              >
                {saving ? "Saving..." : "Click here to select user"}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* User Selection Modal */}
      <UserSelectionModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={currentModalType?.title || ""}
        users={data}
        onSelect={handleUserSelect}
        selectedUserId={selectedUsers[modalState.type]?.id}
      />
    </div>
  );
};

export default AssignFrontendTeam;
